import React from 'react';
import { useDispatch } from 'react-redux';
import { MultiplePricesSwitch } from '../../components/MultiplePricesSwitch/MultiplePricesSwitch';
import { updateRequestFormFields } from '../../consts/actionTypes';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { getRouteSlugs } from '../../helpers/routing';
import { __ } from '../../helpers/TranslationService';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { ICabinInfo, IFormattedCabin, IFormattedCabins, IRate, ISail } from '../../interfaces';
import { IExchangeRate } from '../../interfaces/IHeader';
import { CruisePageActionCreators } from '../../reducers/cruisePage/action-creators';
import { getCurrencyString } from '../PriceString';
import { ImgWithFallback } from '../ImgWithFallback';

interface IRequestFormCabins {
    sails: ISail[];
    exchangeRate?: IExchangeRate;
    cabins: IFormattedCabins;
    selectedCabinId?: number;
    onToogle?: (event: any) => void;
    isAllInclusiveSelected?: boolean;
    selectedRates?: IRate[];
}

const RequestFormCabins = ({
    cabins,
    sails,
    exchangeRate,
    selectedCabinId,
    selectedRates,
    onToogle,
    isAllInclusiveSelected,
}: IRequestFormCabins) => {
    const { pageData, cruisePage } = useTypedSelector((state) => state);

    const cabinTypes = Object.entries(cabins || {}).filter(
        ([key]) => cabins[key].cabins?.length > 0,
    );

    const cabinRates = selectedRates?.find((rate) => rate.cabintype_id === selectedCabinId);
    const allInPrice = cabinRates?.all_in_price?.per_person_price;
    const showAllInSwitcher = !!allInPrice;
    const isCheapestPriceAllIn = cabinRates?.cheapest_price.is_all_inclusive;

    const slugs = getRouteSlugs(pageData.brandConfig.slugs);
    const allInclusiveUrl = slugs.allInclusivePage;

    return (
        <>
            <div className="requestFormContainer__cabins">
                <div className="requestFormContainer__section-label">
                    {__('Cabin', 'dreamlines')}
                </div>
                <div>
                    <p>
                        <b>{__('selectCabins', 'dreamlines')}</b>
                    </p>

                    <div className="cabinsSelectorContainer">
                        {cabinTypes.map(([typeName, cabinType], i) => {
                            return (
                                <CabinItem
                                    sailsAvailable={sails.length > 0}
                                    discountThreshold={pageData.discountThreshold}
                                    key={`cabin-type-${i}`}
                                    typeName={typeName}
                                    cabinType={cabinType}
                                    exchangeRate={exchangeRate}
                                    cabins={cabins}
                                    selectedCabinId={selectedCabinId}
                                    selectedRates={selectedRates}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
            {showAllInSwitcher && (
                <MultiplePricesSwitch
                    allInclusiveUrl={allInclusiveUrl}
                    allInclusiveDescription={cruisePage.ship.allInclusiveDescription}
                    isAllInclusiveSelected={isAllInclusiveSelected}
                    onToogle={onToogle}
                    disabled={isCheapestPriceAllIn}
                />
            )}
        </>
    );
};

interface ICabinItem {
    discountThreshold: number;
    typeName: string;
    cabinType: IFormattedCabin;
    exchangeRate: IExchangeRate;
    cabins: IFormattedCabins;
    sailsAvailable: boolean;
    selectedCabinId?: number;
    isAllInclusive?: boolean;
    selectedRates?: IRate[];
}

const CabinItem = (props: ICabinItem) => {
    const dispatch = useDispatch();
    const {
        typeName,
        cabinType,
        exchangeRate,
        cabins,
        sailsAvailable,
        selectedCabinId,
        selectedRates,
        isAllInclusive,
        discountThreshold,
    } = props;
    const { image, cabinNid } = cabinType;
    const isDualCurrency = exchangeRate !== null;

    const referenceCabin: ICabinInfo = cabins[typeName].cabins.find(
        (cabin) => cabin.cabinType.nid === cabinNid,
    );

    if (!referenceCabin) {
        return null;
    }

    const cabinRates = selectedRates?.find((e) => e.cabintype_id === referenceCabin.cabinType.nid);

    const allInPrice = cabinRates?.all_in_price?.per_person_price;
    const isAllInPrice = !!allInPrice;

    const isCabinSelected =
        cabinType.cabins.findIndex((cabin) => cabin.cabinType.nid === selectedCabinId) >= 0;

    const handleCabinChange = (typeName: string): void => {
        if (!sailsAvailable) {
            return;
        }
        eventTracker.track({
            category: 'cruisepage-selected cabin',
            action: `${typeName === 'suite' ? `${typeName}-picture` : `${typeName} cabin-picture`}`,
            label: ' ',
        });
        dispatch(CruisePageActionCreators.setCabin(cabinNid));
        dispatch({
            type: updateRequestFormFields,
            payload: { cabinNid: cabinNid },
        });
    };

    return (
        <div
            className={`cabinItem ${isCabinSelected ? 'cabinItem--active' : ''}`}
            onClick={() => handleCabinChange(typeName)}
        >
            <ImgWithFallback
                imageUrl={image.original}
                alt="cabinType"
                className="cabinItem__img"
                imageFallback="cabin"
            />
            <div className="cabinItem__info">
                <span className="cabinItem__type">
                    <b>{__(typeName, 'dreamlines')}</b>
                </span>
                {!isAllInclusive || !isCabinSelected || !isAllInPrice ? (
                    <span className="cabinItem__price">
                        {!isDualCurrency
                            ? referenceCabin.cabinType.labels.priceTable.labelMainCurrency
                            : referenceCabin.cabinType.labels.priceTable.labelDualCurrency}
                    </span>
                ) : (
                    <span className="cabinItem__price">
                        {` ${__('from', 'dreamlines')}`}{' '}
                        {getCurrencyString(allInPrice, referenceCabin?.currency)}
                        {` ${__('p.P.', 'dreamlines')}`}
                    </span>
                )}
            </div>
            {referenceCabin.discountPercentage >= discountThreshold ? (
                <div className="cabinItem__discount">
                    <span className="cabinItem__discount__text">
                        -{referenceCabin.discountPercentage}%
                    </span>
                </div>
            ) : null}
            <div className="cabinItem__triangle"></div>
        </div>
    );
};

export default RequestFormCabins;
